import React, { useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

function StopChip({ name }) {
  return (
    <Box
      sx={{
        flexShrink: 0,
        px: 1.5,
        py: 0.75,
        borderRadius: 999,
        bgcolor: '#fff',
        border: '1px solid #c5cae9',
        boxShadow: '0 1px 3px rgba(63,81,181,0.12)',
        maxWidth: 160,
      }}
    >
      <Typography
        fontSize={13}
        fontWeight={700}
        color="#1a237e"
        sx={{
          lineHeight: 1.35,
          textAlign: 'center',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {name}
      </Typography>
    </Box>
  );
}

function NextStopsCarousel({ stops, t }) {
  const scrollRef = useRef(null);
  const dragRef = useRef({ active: false, startX: 0, scrollLeft: 0 });

  const handlePointerDown = (event) => {
    if (event.pointerType !== 'mouse') return;
    const el = scrollRef.current;
    if (!el) return;
    dragRef.current = { active: true, startX: event.clientX, scrollLeft: el.scrollLeft };
    el.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event) => {
    if (!dragRef.current.active || event.pointerType !== 'mouse') return;
    const el = scrollRef.current;
    if (!el) return;
    event.preventDefault();
    el.scrollLeft = dragRef.current.scrollLeft - (event.clientX - dragRef.current.startX);
  };

  const finishDrag = (event) => {
    const el = scrollRef.current;
    if (!el || !dragRef.current.active) return;
    dragRef.current.active = false;
    if (el.hasPointerCapture(event.pointerId)) {
      el.releasePointerCapture(event.pointerId);
    }
  };

  if (!stops?.length) return null;

  return (
    <Box
      role="region"
      aria-label={t ? t('schedule.nextStopsTitle') : undefined}
      sx={{ px: 1.5, pb: 1.5, pt: 0.25 }}
    >
      <Box
        ref={scrollRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishDrag}
        onPointerCancel={finishDrag}
        sx={{
          px: 1.25,
          py: 1.25,
          borderRadius: 2,
          bgcolor: '#f0f4ff',
          border: '1px solid #e0e7ff',
          overflowX: 'auto',
          overflowY: 'hidden',
          WebkitOverflowScrolling: 'touch',
          overscrollBehaviorX: 'contain',
          touchAction: 'pan-x',
          userSelect: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
          cursor: 'grab',
          '&:active': { cursor: 'grabbing' },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.75}
          sx={{ flexWrap: 'nowrap', minWidth: 'min-content' }}
        >
          {stops.map((name, idx) => (
            <React.Fragment key={`${name}-${idx}`}>
              {idx > 0 && (
                <ArrowForwardIcon
                  sx={{
                    fontSize: 20,
                    color: '#7986cb',
                    flexShrink: 0,
                    mx: 0.25,
                  }}
                  aria-hidden
                />
              )}
              <StopChip name={name} />
            </React.Fragment>
          ))}
        </Stack>
      </Box>
    </Box>
  );
}

export default NextStopsCarousel;
